import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { RESTAURANT } from '@src/Constants';
import CustomButton from '@src/components/CustomButton/CustomButton';
import MultiSelect from '@src/components/CustomSelect/MultiSelect';
import CustomSwitch from '@src/components/CustomSwitch/CustomSwitch';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { ModalCloseIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { cleanObject, generateUniqueID, validateFileSize } from '@src/helpers/Helpers';
import { CustomAlertType, Image, Option } from '@src/interfaces/type';
import { useAppSelector } from '@src/store/hooks';
import { AxiosError, AxiosResponse } from 'axios';
import React, { FC, useState } from 'react';
import styled from 'styled-components';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 24px;
	width: 100%;
	height: 100%;
	margin: 24px;
	@media screen and (max-width: 600px) {
		margin: 10px;
	}
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
`;

interface Branch {
	id: number;
	name: string;
	phone?: string;
	address: string;
	supplier_create_order: boolean;
	image?: string;
	created_at?: string;
	organization_id?: number;
	is_deleted?: boolean;
}

interface RestaurantCreationProps {
	setAlert: (data: CustomAlertType) => void;
	setApiCall: (data: boolean) => void;
	setOpenDrawer: (data: boolean) => void;
	displayCustomError: any;
	windowDimensions?: any;
}

const BranchCreation: FC<RestaurantCreationProps> = ({
	setApiCall,
	setAlert,
	setOpenDrawer,
	displayCustomError,
	windowDimensions,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);

	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);

	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;

	const [branch, setBranch] = useState<Branch>({
		id: 0,
		name: '',
		address: '',
		supplier_create_order: false,
	});
	const [disable, setDisable] = useState(false);
	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [images, setImages] = useState<Image[]>([]);
	const [selectedCities, setSelectedCities] = useState<Option[]>([]);

	const indiaCitiesOptions = [
		{ id: '1', value: 'Mumbai' },
		{ id: '2', value: 'Delhi' },
		{ id: '3', value: 'Bangalore' },
		{ id: '4', value: 'Hyderabad' },
		{ id: '5', value: 'Chennai' },
	];

	const handleCityChange = (selectedOptions: Option[]) => {
		setSelectedCities(selectedOptions);
	};

	const disableButton = () => {
		if (!branch?.name || !branch?.address) {
			return true;
		}
		return false;
	};

	const handleSwitchToggle = () => {
		setBranch((prevState: Branch) => {
			return { ...prevState, supplier_create_order: !prevState?.supplier_create_order };
		});
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const files = event?.target?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};
	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = event.dataTransfer?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};

	const uploadImage = () => {
		const file = files[0];
		const formData = new FormData();
		formData.append('file', file);
		const uploadPromise: any = new Promise((resolve, reject) => {
			apiService?.uploadBranchImage(
				formData,
				(response: AxiosResponse) => {
					resolve(response?.data?.data?.url);
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong');
					reject(error);
				}
			);
		});
		return uploadPromise;
	};

	const createBranch = async (branch: Branch) => {
		setDisable(true);
		const restaurantImage = files?.length ? await uploadImage() : '';
		const data = isRestaurant
			? {
					name: branch?.name,
					address: branch?.address,
					image: restaurantImage,
					supplier_create_order: branch?.supplier_create_order,
			  }
			: {
					name: branch?.name,
					address: branch?.address,
					image: restaurantImage,
			  };
		apiService.createBranch(
			cleanObject(data),
			(response: AxiosResponse) => {
				setAlert({
					message: `${isRestaurant ? 'Dealer' : 'Supplier'} Created Successfully`,
					severity: 'success',
				});
				setDisable(false);
				setApiCall(true);
				setOpenDrawer(false);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setDisable(false);
			}
		);
	};

	const fieldStyles = { width: windowDimensions?.width > 800 ? '569px' : '95vw', height: '36px' };
	return (
		<StyledContainer>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				p={2}
				width="100%"
				height="56px"
			>
				<Typography variant="body1">{`Create New ${
					isRestaurant ? 'Dealer' : 'Supplier'
				}`}</Typography>
				<Box display="flex" alignItems="center" gap="15px">
					<CustomButton
						title={
							disable ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Create'
							)
						}
						color="primary"
						styles={{ height: '36px', width: '80px' }}
						handleClick={() => createBranch(branch)}
						disabled={disableButton()}
					/>
					<Box sx={{ cursor: 'pointer' }} onClick={() => setOpenDrawer(false)}>
						<ModalCloseIcon />
					</Box>
				</Box>
			</Box>
			<Divider style={{ width: '100%' }} />
			<StyledForm>
				<LabeledTextField
					label="Branch Name"
					value={branch?.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						setBranch({ ...branch, name: e?.target?.value as string })
					}
					placeholder="e.g Lazy Suzy"
					styles={fieldStyles}
					required
				/>
				<LabeledTextField
					label="Branch Address"
					value={branch?.address}
					onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						setBranch({ ...branch, address: e?.target?.value as string })
					}
					placeholder="type here..."
					styles={fieldStyles}
					required
				/>
				{isRestaurant ? (
					<Box
						sx={{
							...fieldStyles,
							display: 'flex',
							alignItems: 'center',
							gap: '20px',
						}}
					>
						<StyledLabel>Allow Dealer To Create Order</StyledLabel>
						<CustomSwitch
							title=""
							color="primary"
							checked={branch?.supplier_create_order}
							onToggle={handleSwitchToggle}
						/>
					</Box>
				) : null}

				{!isRestaurant ? (
					<MultiSelect
						selectedOptions={selectedCities}
						setSelectedOptions={setSelectedCities}
						options={indiaCitiesOptions}
						onChange={handleCityChange}
					/>
				) : null}
				<Box display="flex" flexDirection="column" gap="5px">
					<StyledLabel>Attach Image</StyledLabel>
					{images?.length ? (
						<ImageThumnail
							images={images}
							setImages={setImages}
							setFiles={setFiles}
							handleFileChange={handleFileChange}
							maxLength={1}
							style={{ width: windowDimensions?.width > 800 ? '569px' : '95vw' }}
						/>
					) : (
						<DragDrop
							handleFileChange={handleFileChange}
							handleFileDrop={handleFileDrop}
							text="Select image to upload"
							info="Upload image in png or jpg format."
							style={{ width: windowDimensions?.width > 800 ? '569px' : '95vw' }}
						/>
					)}
				</Box>
			</StyledForm>
		</StyledContainer>
	);
};

export default BranchCreation;
